import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';

export interface Organization {
  id: number;
  name: string;
  slug: string;
  display_name?: string;
  logo?: string;
  primary_color?: string;
  secondary_color?: string;
  custom_domain?: string;
  tier?: string;
}

interface TenantContextType {
  selectedOrgId: number | null;
  setSelectedOrgId: (id: number | null) => void;
  selectedOrg: Organization | null;
  setSelectedOrg: (org: Organization | null) => void;
  isDomainBased: boolean;
  getOrgPath: (path: string) => string;
  isLoadingOrg: boolean;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedOrg, setSelectedOrgState] = useState<Organization | null>(null);
  const [selectedOrgId, setSelectedOrgIdState] = useState<number | null>(null);
  const [isDomainBased, setIsDomainBased] = useState(false);
  const [isLoadingOrg, setIsLoadingOrg] = useState(true);
  const location = useLocation();

  const setSelectedOrg = (org: Organization | null) => {
    setSelectedOrgState(org);
    setSelectedOrgIdState(org ? org.id : null);
  };

  const setSelectedOrgId = (id: number | null) => {
    setSelectedOrgIdState(id);
    if (!id) setSelectedOrgState(null);
  };

  useEffect(() => {
    const resolveTenant = async () => {
      const hostname = window.location.hostname.toLowerCase();
      
      // Main portal hostnames that don't trigger subdomain resolution
      const mainHosts = ['localhost', '127.0.0.1', 'estipana.localhost', 'www.estipana.localhost', 'estipana.com', 'www.estipana.com', 'estipana.ausxsystems.com', 'staging.estipana.com', 'capacitor'];

      // Capacitor native mobile apps use single-origin domain routing
      if (Capacitor.isNativePlatform()) {
        setIsDomainBased(true);
        setIsLoadingOrg(false);
        return;
      }

      const isMainPortal = mainHosts.includes(hostname);
      
      if (!isMainPortal) {
        setIsDomainBased(true);
        try {
          const res = await fetch(`/api/core/organizations/resolve_domain/?host=${hostname}`);
          if (res.ok) {
            const data = await res.json();
            if (data.id) {
              setSelectedOrgState(data);
              setSelectedOrgIdState(data.id);
            }
          }
        } catch (err) {
          console.error("Failed to resolve organization from hostname:", err);
        }
      } else {
        setIsDomainBased(false);
        // Pathname-based resolution (e.g. /:orgSlug/...)
        const pathParts = location.pathname.split('/');
        const slug = pathParts[1];
        const PUBLIC_PATHS = [
          'login', 'register', 'pricing', 'privacy', 'terms', 'faq', 'contact', 'verify-email', 
          'old-dashboard', 'system-admin', 'admin', 'launchpad', 'home', 'dashboard', 'projects',
          'surveys', 'assignments', 'submissions', 'interviews', 'dashboard-builder', 'dashboards',
          'field-achievement', 'my-tasks', 'team', 'locations', 'external-lists', 'roles',
          'announcements', 'support', 'settings', 'notifications', 's', 'd', 'public'
        ];
        
        if (slug && !PUBLIC_PATHS.includes(slug)) {


          if (selectedOrg && selectedOrg.slug === slug) {
            setIsLoadingOrg(false);
            return;
          }
          try {
            // resolve_domain view accepts subdomain format. We pass `${slug}.localhost` to resolve via subdomain logic.
            const res = await fetch(`/api/core/organizations/resolve_domain/?host=${slug}.localhost`);
            if (res.ok) {
              const data = await res.json();
              if (data.id) {
                setSelectedOrgState(data);
                setSelectedOrgIdState(data.id);
              }
            } else {
              setSelectedOrgState(null);
              setSelectedOrgIdState(null);
            }
          } catch (err) {
            console.error("Failed to resolve organization from slug:", err);
            setSelectedOrgState(null);
            setSelectedOrgIdState(null);
          }
        } else {
          setSelectedOrgState(null);
          setSelectedOrgIdState(null);
        }
      }
      setIsLoadingOrg(false);
    };

    resolveTenant();
  }, [location.pathname]);

  const getOrgPath = (path: string) => {
    const formattedPath = path.startsWith('/') ? path : `/${path}`;
    if (isDomainBased) {
      return formattedPath;
    }
    
    // Extract slug from active org or valid URL path segment
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    const firstSegment = pathParts[0];

    const PUBLIC_PATHS = [
      'login', 'register', 'pricing', 'privacy', 'terms', 'faq', 'contact', 'verify-email', 
      'old-dashboard', 'system-admin', 'admin', 'launchpad', 'home', 'dashboard', 'projects',
      'surveys', 'assignments', 'submissions', 'interviews', 'dashboard-builder', 'dashboards',
      'field-achievement', 'my-tasks', 'team', 'locations', 'external-lists', 'roles',
      'announcements', 'support', 'settings', 'notifications', 's', 'd', 'public', 'billing', 'profile'
    ];

    const validSlug = selectedOrg?.slug || (firstSegment && !PUBLIC_PATHS.includes(firstSegment) ? firstSegment : null);

    if (!validSlug) {
      return formattedPath;
    }

    if (formattedPath.startsWith(`/${validSlug}/`) || formattedPath === `/${validSlug}`) {
      return formattedPath;
    }
    
    const cleanSubpath = formattedPath === '/' ? '' : formattedPath;
    return `/${validSlug}${cleanSubpath}`;
  };

  return (
    <TenantContext.Provider value={{ 
      selectedOrgId, 
      setSelectedOrgId, 
      selectedOrg, 
      setSelectedOrg, 
      isDomainBased, 
      getOrgPath,
      isLoadingOrg 
    }}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};
